/**
 * LeftShield Admin - Authenticated Route Component
 *
 * Route wrapper that redirects unauthenticated users to login.
 */
import React from 'react';
import { Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { LoginPage } from './LoginPage';

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

export interface AuthenticatedRouteProps {
  children: React.ReactNode;
}

/**
 * Wrapper component that ensures the user is authenticated.
 * Shows LoginPage if not authenticated, loading spinner during auth check.
 */
export const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({ children }) => {
  const styles = useStyles();
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  // Show loading during authentication check
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Checking authentication..." />
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // User is authenticated, render children
  return <>{children}</>;
};

export default AuthenticatedRoute;
